import { Table, TablePaginationConfig } from 'antd';
import { FilterValue, SorterResult } from 'antd/lib/table/interface';
import React, { Key } from 'react';
import { TableColumn } from '.';
import { FetchGridParameters } from '../../Networking';
import { getColumns } from './tableColumns';

export interface FilteredTableProps<DataType extends object> {
  /**
   * An array of sorted, filtered and paged data to display on the table
   *  */
  data?: DataType[];
  /** An error to display on the table. Overrides `data` and `emptyMessage` */
  error?: React.ReactNode;
  /** Specify a row key for the given data type */
  rowKey?: (item: DataType, index?: number) => React.Key;
  /** The total number of _filtered_ items across all pages of the table */
  total?: number;
  /**
   * The paging, filtering and sorting settings of the table; gets displayed
   * on the column headers and the paginator
   */
  params?: FetchGridParameters<DataType>;
  /**
   * The different page size options. If left undefined, the pagesize dropdown
   * is hidden
   */
  pageSizeOptions?: number[];
  /** An array of table columns */
  columns: TableColumn<DataType>[];
  /** Whether or not the table data is selectable */
  isSelectable?: boolean;
  /** Selection of table row items */
  selected?: Key[];
  /** A callback that fires every time the selection changes */
  onSelect?: (selectedRowKeys: Key[], selectedRows: DataType[]) => void;
  /** Whether or not the table data is currently loading */
  isLoading?: boolean;
  /** Content to display above the table and to the left of the reset filters button */
  header?: React.ReactNode;
  /** The raw height of the table. Leaving it undefined results in a full height table with no scrolling */
  height?: number;
  /** Content to display when the table has no data or error */
  emptyMessage?: React.ReactNode;
  /** Callback for whenever the sorting, filtering or pagination has changed from within the table controls */
  onChange?: (params: FetchGridParameters<DataType>) => void;
}

export const FilteredTable = <DataType extends object>(
  props: FilteredTableProps<DataType> & { children?: React.ReactNode }
) => {
  const {
    data,
    total,
    params,
    pageSizeOptions,
    columns,
    rowKey,
    isSelectable,
    selected,
    onSelect,
    isLoading,
    header,
    height,
    emptyMessage,
    error,
    onChange,
  } = props;

  function applyParams(
    pagination: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    sorter: SorterResult<DataType> | SorterResult<DataType>[]
  ) {
    if (!onChange) {
      return;
    }

    let singleSort = sorter as SorterResult<DataType>;

    onChange({
      ...params,
      page: pagination.current!,
      pageSize: pagination.pageSize!,
      orderKey: singleSort.field as keyof DataType,
      order: singleSort.order == 'descend' ? 'desc' : 'asc',
    });
  }

  function modifyFilters(filters?: FetchGridParameters<DataType>['filters']) {
    if (onChange && params) {
      onChange({
        ...params,
        page: 1,
        filters,
      });
    }
  }

  return (
    <Table
      title={header ? () => header : undefined}
      columns={getColumns<DataType>(columns, params, modifyFilters)}
      dataSource={error ? [] : data}
      rowKey={rowKey}
      loading={isLoading}
      scroll={{ x: '100%', y: height }}
      pagination={{
        current: params?.page ?? 1,
        pageSize: params?.pageSize,
        pageSizeOptions: pageSizeOptions?.map((s) => s.toFixed()),
        showSizeChanger: pageSizeOptions != null,
        total: total,
      }}
      locale={{
        emptyText: error ?? emptyMessage,
      }}
      onChange={applyParams}
      rowSelection={
        isSelectable
          ? {
              selectedRowKeys: selected,
              onChange: onSelect,
            }
          : undefined
      }
    />
  );
};
